import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useRBAC } from '../../contexts/RBACContext';

const Sidebar = ({ isCollapsed = false, isMobileOpen = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getSidebarItems } = useRBAC();

  const navigationItems = getSidebarItems();

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      if (onToggleCollapse && window.innerWidth < 1024) {
        onToggleCollapse();
      }
    }
  };

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const showExpanded = isMobile ? true : !isCollapsed;

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-screen bg-card/95 backdrop-blur-xl border-r border-border z-50 transition-all duration-300 shadow-xl flex flex-col
          ${showExpanded ? 'w-60' : 'w-20'}
          ${isMobile ? (isMobileOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border min-h-[56px]">
          <div className="flex-shrink-0">
            <img
              src="/assets/images/logo_PNG_RGB-1776767805644.png"
              alt="Electronic City for IT Services"
              className={`object-contain transition-all duration-300 ${showExpanded ? 'h-9 w-auto' : 'w-8 h-8'}`}
            />
          </div>
          {showExpanded && (
            <span className="font-semibold text-sm truncate">eCity Pro</span>
          )}
        </div>

        {/* Navigation — fills remaining height and scrolls */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-2 px-2">
          <ul className="space-y-0.5">
            {navigationItems?.map((item) => {
              if (item?.isSectionHeader) {
                return (
                  <li key={item?.id} className={`${showExpanded ? 'pt-3 pb-1' : 'pt-2 pb-1'}`}>
                    {showExpanded ? (
                      <div className="flex items-center gap-2 px-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{item?.label}</span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                    ) : (
                      <div className="h-px bg-border mx-2" />
                    )}
                  </li>
                );
              }
              return (
                <li key={item?.id}>
                  <button
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-[13px] font-medium transition-all duration-200
                      ${isActive(item?.path)
                        ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-md'
                        : 'text-foreground hover:bg-muted hover:translate-x-0.5'
                      }
                      ${!showExpanded ? 'justify-center px-0' : ''}
                    `}
                    title={!showExpanded ? item?.tooltip || item?.label : ''}
                    aria-current={isActive(item?.path) ? 'page' : undefined}
                  >
                    <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                    {showExpanded && <span className="truncate">{item?.label}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Collapse toggle (desktop only) */}
        {!isMobile && (
          <div className="flex-shrink-0 p-3 border-t border-border">
            <button
              onClick={onToggleCollapse}
              className={`w-full flex items-center h-9 px-2.5 rounded-lg text-sm font-medium bg-muted hover:bg-muted/80 transition-all duration-200 active:scale-95
                ${showExpanded ? 'gap-2 justify-start' : 'justify-center'}
              `}
              aria-label={showExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <Icon name={showExpanded ? 'ChevronsLeft' : 'ChevronsRight'} size={16} />
              {showExpanded && <span className="text-[13px]">Collapse</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;